/**
 * MetricTrendsSection — the Metric Trends charts as a linked area INSIDE the
 * three-year model on /2027-plan (quick-261013-hqb, John 2026-10-13: "move it
 * from being a standalone tab to being a linked area referenced in the model,
 * and lets extend the metrics to cover ytd, looking at each aggregated quarter").
 *
 * Was the standalone /metric-trends page (quick-261002-be8). Now: one section at
 * the foot of the model, every chart anchored (#trend-<key>) so the "trend ↓"
 * link beside each input box jumps to its chart, and the view rolls the monthly
 * series up by calendar quarter (default) or shows it month by month, year to
 * date. Each card prints the YTD pooled value and n.
 *
 * Data: `FUNNEL_TRENDS` — a dated monthly series generated from compact
 * Salesforce pulls by ~/funnel-benchmarks/trends-live.js; the roll-ups are pure
 * (shared/plan2027/trend-periods). A dashed reference line is the model's
 * current default; a partial period is hollow (○); a lead cohort still inside
 * the 60-day maturation window is marked ◐.
 */
import { FUNNEL_TRENDS } from '@saledash/shared/plan2027/funnel-trends';
import { FUNNEL_BASELINE_2026_09 } from '@saledash/shared/plan2027/model3y';
import { periodLabel, periods, yearToDate, type TrendKey, type TrendPeriod, type TrendPeriodMode } from '@saledash/shared/plan2027/trend-periods';
import ReactECharts from 'echarts-for-react';
import { useMemo, useState } from 'react';

type Unit = 'pct' | 'count' | 'ratio' | 'money' | 'days';

interface MetricDef {
  key: TrendKey;
  title: string;
  unit: Unit;
  /** The three-year model's current default for this input; null = the model reads it live, no reference line. */
  modelDefault: number | null;
  /** What num / den are, for the n label. */
  nLabel: string;
  /** Which box on the plan tab this feeds. */
  feeds: string;
  /** Lower is better (conversations per meeting, cycle days) — flips the change colour. */
  lowerIsBetter?: boolean;
}

interface Group {
  id: string;
  title: string;
  note: string;
  metrics: MetricDef[];
}

const fb = FUNNEL_BASELINE_2026_09;

export const GROUPS: Group[] = [
  {
    id: 'inbound',
    title: 'Inbound funnel',
    note: 'Feeds the inbound side of the new-logo funnel: leads → booked → delivered → opportunity.',
    metrics: [
      { key: 'inboundLeadsPerMonth', title: 'Inbound leads per month', unit: 'count', modelDefault: null, nLabel: 'non-disqualified inbound leads by source month (model reads MAIN-27 live, ÷ 3); a quarter shows the monthly average, n = the total', feeds: 'Inbound leads / month' },
      { key: 'leadToBooked', title: 'Inbound lead → booked meeting', unit: 'pct', modelDefault: fb.leadToBooked, nLabel: 'booked / non-DQ inbound leads by source month', feeds: 'Lead → booked meeting' },
      { key: 'inboundDeliveredOfBooked', title: 'Inbound booked → delivered', unit: 'pct', modelDefault: fb.inboundDeliveredOfBooked, nLabel: 'delivered / booked (lead cohort)', feeds: 'Booked → delivered' },
      { key: 'inboundMtgToOpp', title: 'Inbound delivered meeting → opportunity', unit: 'pct', modelDefault: fb.inboundMtgToOpp, nLabel: 'opps / delivered Inbound New meetings by meeting month', feeds: 'Delivered meeting → opportunity' },
    ],
  },
  {
    id: 'outbound',
    title: 'Outbound funnel',
    note: `Feeds the outbound side: conversations → booked → delivered → opportunity. Conversations are split by who dialed, matching the two boxes on the plan tab: BDR calls ÷ the BDRs who logged one that month, and the AEs' own calls ÷ ${String(FUNNEL_TRENDS.sellers)} quota-carrying sellers.`,
    metrics: [
      { key: 'conversationsPerBdr', title: 'Conversations per BDR per month', unit: 'count', modelDefault: fb.bdrConversationsPerMonth / fb.bdrs, nLabel: 'BDR conversations / BDRs who logged one; a quarter shows the monthly average', feeds: 'Conversations per BDR per month' },
      { key: 'conversationsPerSeller', title: 'Conversations per AE per month (their own dials)', unit: 'count', modelDefault: fb.sellerConversationsPerMonth / fb.sellers, nLabel: 'AE + sales-manager conversations / quota sellers; a quarter shows the monthly average', feeds: 'Conversations per AE per month (their own dials)' },
      { key: 'conversationsPerMeeting', title: 'Conversations per booked meeting', unit: 'ratio', modelDefault: fb.conversationsPerMeeting, nLabel: 'conversations / Outbound New meetings booked', feeds: 'Conversations per booked meeting (efficiency)', lowerIsBetter: true },
      { key: 'outboundDeliveredOfDue', title: 'Outbound booked → delivered', unit: 'pct', modelDefault: fb.outboundDeliveredOfBooked, nLabel: 'delivered / come due, by meeting month', feeds: 'Booked → delivered' },
      { key: 'outboundMtgToOpp', title: 'Outbound delivered meeting → opportunity', unit: 'pct', modelDefault: fb.outboundMtgToOpp, nLabel: 'opps / delivered Outbound New meetings by meeting month', feeds: 'Delivered meeting → opportunity' },
    ],
  },
  {
    id: 'deals',
    title: 'Opportunity volume and deal economics (new logo)',
    note: 'Feeds what each opportunity is worth and how often it closes. New Business only, like the model. One or two deals a month: read the n, not the point — the quarter view pools them.',
    metrics: [
      { key: 'newLogoShareOfOpps', title: 'New Business share of opportunities created', unit: 'pct', modelDefault: fb.newLogoShareOfOpps, nLabel: 'New Business / all Product opps created', feeds: 'New Business share of created opps (dated constant)' },
      { key: 'avgCreatedOppValue', title: 'Average created new-logo opportunity', unit: 'money', modelDefault: fb.avgNewLogoCreatedOpp, nLabel: 'sum $ / New Business opps created with an Amount', feeds: 'Average created new-logo opportunity' },
      { key: 'newLogoWinRate', title: 'New-logo win rate, by close month', unit: 'pct', modelDefault: fb.newLogoWinRate, nLabel: 'Closed Won / (Closed Won + Closed Lost), New Business, by stage', feeds: 'New-logo win rate' },
      { key: 'avgWonValue', title: 'Average won new-logo deal', unit: 'money', modelDefault: fb.avgNewLogoDeal, nLabel: 'sum $ / New Business deals won in the period', feeds: 'Average won new-logo deal' },
      { key: 'cycleDays', title: 'Sales cycle, new-logo wins (days)', unit: 'days', modelDefault: null, nLabel: 'median days by month; a quarter or YTD is the mean (sum of days / wins). Model reads agg_plan_cycle_timing live', feeds: 'Sales cycle (days, opp → close)', lowerIsBetter: true },
    ],
  },
];

export const NOT_CHARTED = [
  'BDRs, quota-carrying sellers (AEs), their ramps and AE meeting capacity — headcount assumptions',
  'BDRs and AEs added, inbound lead growth, conversion improvement, win-rate & deal-size drift — plan levers, not measurements',
  'Pipeline in hand — a Salesforce snapshot, not a rate',
  'Starting ARR, expansion dollars, churn + contraction — Rillet, annual',
];

function fmt(unit: Unit, v: number | null): string {
  if (v == null) return '—';
  if (unit === 'pct') return `${(v * 100).toFixed(1)}%`;
  if (unit === 'count') return Math.round(v).toLocaleString('en-US');
  if (unit === 'money') return v >= 1000 ? `$${(v / 1000).toFixed(v >= 100_000 ? 0 : 1)}k` : `$${Math.round(v).toLocaleString('en-US')}`;
  if (unit === 'days') return `${String(Math.round(v))} d`;
  return (Math.round(v * 10) / 10).toFixed(1);
}
/** Chart-space value: percentages plot as 0..100, money as $k. */
function toAxis(unit: Unit, v: number | null): number | null {
  if (v == null) return null;
  if (unit === 'pct') return v * 100;
  if (unit === 'money') return v / 1000;
  return v;
}
function axisLabel(unit: Unit, v: number): string {
  if (unit === 'pct') return `${String(Math.round(v))}%`;
  if (unit === 'money') return `$${v.toLocaleString('en-US')}k`;
  if (unit === 'days') return `${String(Math.round(v))}d`;
  return v.toLocaleString('en-US');
}
function pointLabel(unit: Unit, v: number): string {
  if (unit === 'pct') return `${v.toFixed(1)}%`;
  if (unit === 'money') return `$${v.toFixed(v >= 100 ? 0 : 1)}k`;
  if (unit === 'days') return `${String(Math.round(v))} d`;
  if (unit === 'count') return Math.round(v).toLocaleString('en-US');
  return v.toFixed(1);
}
/** The n under a point: "num/den" for rates, "n den" for means, the total for leads. */
function nText(def: MetricDef, p: { num: number; den: number }): string {
  if (def.unit === 'money' || def.unit === 'days') return `n ${String(p.den)}`;
  if (def.key === 'inboundLeadsPerMonth') return String(p.num);
  return `${String(p.num)}/${String(p.den)}`;
}

function buildOption(def: MetricDef, series: TrendPeriod[]): Record<string, unknown> {
  const pts = series.map((s) => s[def.key]);
  const values = pts.map((p) => toAxis(def.unit, p.value));
  const ref = def.modelDefault == null ? null : toAxis(def.unit, def.modelDefault);
  const cats = series.map((s) => periodLabel(s));
  const all = values.filter((v): v is number => v != null).concat(ref == null ? [] : [ref]);
  const maxV = Math.max(...all, 0) * 1.25 || 1;
  return {
    grid: { left: 48, right: 16, top: 28, bottom: 44 },
    tooltip: {
      trigger: 'axis',
      formatter: (params: { dataIndex: number }[]) => {
        const i = params[0]?.dataIndex ?? 0;
        const s = series[i];
        const p = pts[i];
        if (!s || !p) return '';
        const flags = [s.partialMonth ? (s.kind === 'quarter' ? 'quarter in progress' : 'partial month') : '', p.immature ? 'cohort still maturing' : ''].filter(Boolean).join(' · ');
        const modelTxt = def.modelDefault == null ? 'model reads this live' : `model default ${fmt(def.unit, def.modelDefault)}`;
        const span = s.kind === 'quarter' ? `<br/><span style="color:#64748b">${s.months.map((m) => periodLabel({ month: m, kind: 'month' })).join(', ')}</span>` : '';
        return `<b>${periodLabel(s)}</b>${span}<br/>${def.title}: <b>${fmt(def.unit, p.value)}</b><br/>n = ${String(p.num)} / ${String(p.den)}${flags ? `<br/><span style="color:#92400e">${flags}</span>` : ''}<br/><span style="color:#64748b">${modelTxt}</span>`;
      },
    },
    xAxis: {
      type: 'category',
      data: cats,
      axisLabel: {
        color: '#64748b',
        fontSize: 10,
        interval: 0,
        formatter: (_v: string, i: number) => {
          const s = series[i];
          const p = pts[i];
          if (!s || !p) return '';
          const mark = s.partialMonth ? ' ○' : p.immature ? ' ◐' : '';
          return `{m|${periodLabel(s)}${mark}}\n{n|${nText(def, p)}}`;
        },
        rich: { m: { fontSize: 10, color: '#334155', fontWeight: 600, lineHeight: 14 }, n: { fontSize: 9, color: '#94a3b8', lineHeight: 12 } },
      },
      axisTick: { show: false },
      axisLine: { lineStyle: { color: '#e2e8f0' } },
    },
    yAxis: {
      type: 'value',
      min: 0,
      max: Math.ceil(maxV),
      axisLabel: { color: '#64748b', fontSize: 10, formatter: (v: number) => axisLabel(def.unit, v) },
      splitLine: { lineStyle: { color: '#f1f5f9' } },
    },
    series: [
      {
        name: def.title,
        type: 'line',
        data: values.map((v, i) => ({
          value: v,
          itemStyle: series[i]?.partialMonth ? { color: '#ffffff', borderColor: '#3ccbf8', borderWidth: 2 } : { color: '#3ccbf8' },
        })),
        connectNulls: true,
        symbol: 'circle',
        symbolSize: 8,
        lineStyle: { color: '#3ccbf8', width: 2.5 },
        label: { show: true, position: 'top', fontSize: 10, fontWeight: 600, color: '#0f172a', formatter: (p: { value: number | null }) => (p.value == null ? '' : pointLabel(def.unit, p.value)) },
        ...(ref == null
          ? {}
          : {
              markLine: {
                silent: true,
                symbol: 'none',
                lineStyle: { color: '#111d36', type: 'dashed', width: 1.5 },
                label: { position: 'insideEndTop', fontSize: 9, color: '#111d36', formatter: `model ${fmt(def.unit, def.modelDefault)}` },
                data: [{ yAxis: ref }],
              },
            }),
      },
    ],
  };
}

function TrendCard({ def, series, ytd }: { def: MetricDef; series: TrendPeriod[]; ytd: TrendPeriod | null }): React.JSX.Element {
  const firstIdx = series.findIndex((s) => s[def.key].value != null);
  const first = firstIdx >= 0 ? series[firstIdx]?.[def.key] : undefined;
  const last = series[series.length - 1]?.[def.key];
  const lastFullPeriod = [...series].reverse().find((s) => !s.partialMonth && s[def.key].value != null);
  const lastFull = lastFullPeriod?.[def.key];
  const change = first?.value != null && lastFull?.value != null && first !== lastFull ? lastFull.value - first.value : null;
  const changeTxt =
    change == null ? '' : def.unit === 'pct' ? `${change >= 0 ? '+' : '−'}${(Math.abs(change) * 100).toFixed(1)} pts` : `${change >= 0 ? '+' : '−'}${fmt(def.unit, Math.abs(change))}`;
  const worse = change != null && (def.lowerIsBetter ? change > 0 : change < 0);
  const ytdPt = ytd?.[def.key];
  return (
    <section id={`trend-${def.key}`} className="scroll-mt-20 rounded-[14px] border border-ink-100 bg-white p-4 shadow-[0_1px_2px_rgba(16,28,45,0.05)]" data-testid={`trend-${def.key}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-sm font-bold tracking-tight text-ink-900">{def.title}</h3>
        <span className="text-[11px] tabular-nums text-ink-500">
          {ytd && ytdPt && (
            <span className="mr-2 rounded-full border border-ink-100 bg-ink-50 px-1.5 py-px text-[10px] font-semibold text-ink-700" data-testid="ytd">
              {periodLabel(ytd)} {fmt(def.unit, ytdPt.value)} <span className="font-normal text-ink-500">({nText(def, ytdPt)})</span>
            </span>
          )}
          {first && lastFullPeriod && lastFull && change != null ? (
            <>
              {periodLabel(series[firstIdx] ?? lastFullPeriod)} {fmt(def.unit, first.value)} &rarr; {periodLabel(lastFullPeriod)} {fmt(def.unit, lastFull.value)}{' '}
              <b className={`font-semibold ${worse ? 'text-bad-text' : 'text-good-text'}`}>{changeTxt}</b>
            </>
          ) : null}
          {last?.value != null && last !== lastFull ? <span className="text-ink-500"> &middot; latest {fmt(def.unit, last.value)} (in progress)</span> : null}
        </span>
      </div>
      <div className="h-[210px] w-full" data-testid="trend-chart">
        <ReactECharts option={buildOption(def, series)} style={{ height: '100%', width: '100%' }} opts={{ renderer: 'canvas' }} notMerge lazyUpdate={false} />
      </div>
      <div className="mt-1 flex flex-wrap justify-between gap-x-4 text-[10.5px] leading-tight text-ink-500">
        <span>n = {def.nLabel}</span>
        <span>feeds: {def.feeds}</span>
      </div>
    </section>
  );
}

export interface MetricTrendsSectionProps {
  /** Section number label, e.g. "5 · Metric trends". */
  n?: string;
}

export function MetricTrendsSection({ n = '5 · Metric trends' }: MetricTrendsSectionProps): React.JSX.Element {
  const t = FUNNEL_TRENDS;
  const [mode, setMode] = useState<TrendPeriodMode>('quarter');
  const series = useMemo(() => periods(t, mode), [t, mode]);
  const ytd = useMemo(() => yearToDate(t), [t]);
  const lastMonth = t.series[t.series.length - 1];
  const firstMonth = t.series[0];
  const partial = lastMonth?.partialMonth ?? false;
  const charted = GROUPS.reduce((c, g) => c + g.metrics.length, 0);
  const span = firstMonth && lastMonth ? `${periodLabel({ month: firstMonth.month, kind: 'month' })} – ${periodLabel({ month: lastMonth.month, kind: 'month' })}` : '';

  return (
    <section
      id="metric-trends"
      className="scroll-mt-20 rounded-[14px] border border-ink-100 border-l-4 border-l-ink-900 bg-white p-5 shadow-[0_1px_2px_rgba(16,28,45,0.05)]"
      data-testid="metric-trends"
    >
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="text-[11px] font-bold uppercase tracking-[0.12em] text-ink-500">{n}</span>
        <h2 className="text-base font-bold tracking-tight text-ink-900">How the {String(charted)} measurable inputs have trended this year</h2>
      </div>
      <p className="mt-1 max-w-[94ch] text-xs text-ink-700">
        Feeds nothing by itself: this is the measured history behind the boxes in section 1, year to date ({span}, Salesforce pull {t.pulledOn}),
        so each assumption can be read against what actually happened. Each chart&rsquo;s dashed line is the model&rsquo;s current default ({fb.funnelWindow} for
        funnel stages, {fb.dealWindow} for deal economics); the pill on each card is the year-to-date pooled value and its n. Under each point: n =
        numerator / denominator. A hollow point (○) is a period still in progress{partial ? ` (the pull ends ${t.pulledOn})` : ''}; a half point (◐) is a lead
        cohort still inside the {String(t.matureDays)}-day maturation window, so its lead &rarr; booked rate can still rise.
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-2" role="group" aria-label="Trend period">
        <span className="text-[11px] font-semibold text-ink-500">Show</span>
        {(
          [
            ['quarter', 'By quarter'],
            ['month', 'By month'],
          ] as const
        ).map(([m, label]) => (
          <button
            key={m}
            type="button"
            aria-pressed={mode === m}
            onClick={() => {
              setMode(m);
            }}
            className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${
              mode === m ? 'border-adeptia-cyan bg-adeptia-cyan/15 text-ink-900' : 'border-ink-200 bg-white text-ink-700 hover:border-adeptia-cyan'
            }`}
          >
            {label}
          </button>
        ))}
        <span className="text-[10.5px] text-ink-500" data-testid="period-note">
          {mode === 'quarter'
            ? 'Each quarter pools its months: Σ numerator ÷ Σ denominator for rates and averages; volumes show the monthly average with the quarter total as n.'
            : 'Month by month. Monthly counts are small — read direction, not single points.'}
        </span>
      </div>

      <div className="mt-4 space-y-5">
        {GROUPS.map((g) => (
          <div key={g.id} className="space-y-2" data-testid={`group-${g.id}`}>
            <div>
              <div className="text-[11px] font-bold uppercase tracking-wider text-ink-500">{g.title}</div>
              <p className="text-xs text-ink-700">{g.note}</p>
            </div>
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
              {g.metrics.map((def) => (
                <TrendCard key={def.key} def={def} series={series} ytd={ytd} />
              ))}
            </div>
          </div>
        ))}

        <div className="rounded-[10px] border border-ink-100 bg-ink-50 p-3 text-xs text-ink-700" data-testid="not-charted">
          <div className="text-[11px] font-bold uppercase tracking-wider text-ink-500">Inputs with no monthly measurement</div>
          <ul className="mt-1 list-disc space-y-0.5 pl-5">
            {NOT_CHARTED.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </div>

        <p className="text-[10.5px] leading-tight text-ink-500">
          Definitions match the Funnel Metric Definitions workbook: inbound by Latest_Source_Date__c excluding Disqualified; meetings are Events
          typed Inbound New / Outbound New; delivered = Meeting_Delivered__c; meeting &rarr; opportunity via ConvertedOpportunityId or an
          opportunity created at the same account in the meeting month or the next; conversations = call dispositions Connected, Meeting, Not
          Interested, Call Back Later, Gatekeeper; deals = Product opportunities, name not containing &ldquo;uptick&rdquo;, New Business only, win rate by stage
          with no loss-reason filter. Refresh: re-pull the six compact SOQL (from 1 Jan) into ~/funnel-benchmarks/live, run trends-live.js then
          emit-trends-ts.js, commit the shared constant.
        </p>
      </div>
    </section>
  );
}
